import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { GivingCampaignPartnerDto } from '@percent/partner-dashboard/services/workplaceGiving/workplaceGivingService.types'
import { Table, TableBody, TablePagination, TableProvider } from '@percent/lemonade'
import { ErrorView } from '@percent/partner-dashboard/common/components'
import { CampaignsTableRow } from './CampaignsTableRow/CampaignsTableRow'

export type CampaignsTableProps = Readonly<{
  data: GivingCampaignPartnerDto[]
  isLoading: boolean
  errorMessage: string
  totalResults: number | null
  nextPage: (() => void) | null
  previousPage: (() => void) | null
}>

export function CampaignsTable({
  data,
  isLoading,
  errorMessage,
  totalResults,
  nextPage,
  previousPage
}: CampaignsTableProps) {
  const { t } = useTranslation()

  const columns = useMemo(
    () => [
      { id: t('table.header.name'), props: { width: '25%' } },
      { id: t('table.header.dates'), props: { width: '25%' } },
      { id: t('table.header.groups'), props: { width: '20%' } },
      { id: t('table.header.status'), props: { width: '16.5%' } },
      { id: '', props: { width: '7%' } }
    ],
    [t]
  )

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <TableProvider
      columns={columns}
      totalResults={totalResults}
      isLoading={isLoading || !data}
      illustration="no-results"
      emptyTableTitle={t('typography.noGivingCampaignsTitle')}
      emptyTableDescription={t('typography.noGivingCampaignsDescription')}
    >
      <Table>
        <TableBody>
          {data?.map(campaign => (
            <CampaignsTableRow key={campaign.id} campaign={campaign} />
          ))}
        </TableBody>
      </Table>
      <TablePagination
        totalResultsText={t('table.results', { count: totalResults ?? undefined })}
        previousPage={previousPage}
        nextPage={nextPage}
      />
    </TableProvider>
  )
}
